import { msg } from '@lingui/core/macro';
import { PlaceholderKey } from 'modules/manager/domain/placeholder/placeholder-keys';
import type { MessageDescriptor } from 'shared/i18n';

/**
 * Sections of the key picker list. A layout of the picker, not a property of the
 * data — hence here and not in the registry.
 */
export enum KeyGroup {
  TEAM = 'team',
  MATCH = 'match',
  CHAMPIONSHIP = 'championship',
  STANDING = 'standing',
  PLAYER = 'player',
  PLAYER_STATS = 'playerStats',
}

export const KEY_GROUP_ORDER: readonly KeyGroup[] = [
  KeyGroup.TEAM,
  KeyGroup.MATCH,
  KeyGroup.CHAMPIONSHIP,
  KeyGroup.STANDING,
  KeyGroup.PLAYER,
  KeyGroup.PLAYER_STATS,
];

export const KEY_GROUP_LABELS: Record<KeyGroup, MessageDescriptor> = {
  [KeyGroup.TEAM]: msg`Команда`,
  [KeyGroup.MATCH]: msg`Матч`,
  [KeyGroup.CHAMPIONSHIP]: msg`Турнир и организатор`,
  [KeyGroup.STANDING]: msg`Строка таблицы`,
  [KeyGroup.PLAYER]: msg`Игрок`,
  [KeyGroup.PLAYER_STATS]: msg`Статистика игрока`,
};

export const KEY_GROUPS: Record<PlaceholderKey, KeyGroup> = {
  [PlaceholderKey.TEAM_NAME]: KeyGroup.TEAM,
  [PlaceholderKey.TEAM_SHORT_NAME]: KeyGroup.TEAM,
  [PlaceholderKey.TEAM_LOGO]: KeyGroup.TEAM,
  [PlaceholderKey.SCORE]: KeyGroup.MATCH,
  [PlaceholderKey.SCORE_TEAM]: KeyGroup.MATCH,
  [PlaceholderKey.GOAL_SCORERS]: KeyGroup.MATCH,
  [PlaceholderKey.MATCH_DATE]: KeyGroup.MATCH,
  [PlaceholderKey.MATCH_TIME]: KeyGroup.MATCH,
  [PlaceholderKey.MATCH_VENUE]: KeyGroup.MATCH,
  [PlaceholderKey.ROUND_NAME]: KeyGroup.MATCH,
  [PlaceholderKey.CHAMPIONSHIP_NAME]: KeyGroup.CHAMPIONSHIP,
  [PlaceholderKey.TOURNAMENT_NAME]: KeyGroup.CHAMPIONSHIP,
  [PlaceholderKey.TOURNAMENT_LOGO]: KeyGroup.CHAMPIONSHIP,
  // Stage-level, not a row statistic — it names the table, so it sits with the
  // championship rather than with the row keys.
  [PlaceholderKey.GROUP_NAME]: KeyGroup.CHAMPIONSHIP,
  [PlaceholderKey.TOURNAMENT_POSITION]: KeyGroup.STANDING,
  [PlaceholderKey.GAMES_PLAYED]: KeyGroup.STANDING,
  [PlaceholderKey.GAMES_WON]: KeyGroup.STANDING,
  [PlaceholderKey.GAMES_DRAWN]: KeyGroup.STANDING,
  [PlaceholderKey.GAMES_LOST]: KeyGroup.STANDING,
  [PlaceholderKey.GOALS_SCORED]: KeyGroup.STANDING,
  [PlaceholderKey.GOALS_CONCEDED]: KeyGroup.STANDING,
  [PlaceholderKey.GOAL_DIFFERENCE]: KeyGroup.STANDING,
  [PlaceholderKey.GAMES_DIFFERENCE]: KeyGroup.STANDING,
  [PlaceholderKey.POINTS]: KeyGroup.STANDING,
  [PlaceholderKey.PLAYER_NAME]: KeyGroup.PLAYER,
  [PlaceholderKey.PLAYER_NUMBER]: KeyGroup.PLAYER,
  [PlaceholderKey.PLAYER_PHOTO]: KeyGroup.PLAYER,
  [PlaceholderKey.PLAYER_POSITION]: KeyGroup.PLAYER,
  [PlaceholderKey.PLAYER_GAMES_PLAYED]: KeyGroup.PLAYER_STATS,
  [PlaceholderKey.PLAYER_GOALS]: KeyGroup.PLAYER_STATS,
  [PlaceholderKey.PLAYER_ASSISTS]: KeyGroup.PLAYER_STATS,
  [PlaceholderKey.PLAYER_GOALS_ASSISTS]: KeyGroup.PLAYER_STATS,
  [PlaceholderKey.PLAYER_YELLOW_CARDS]: KeyGroup.PLAYER_STATS,
  [PlaceholderKey.PLAYER_RED_CARDS]: KeyGroup.PLAYER_STATS,
};

/** `keys` split into the picker's sections, in section order; empty sections are dropped. */
export function groupKeys(keys: readonly PlaceholderKey[]): { group: KeyGroup; keys: PlaceholderKey[] }[] {
  return KEY_GROUP_ORDER.map((group) => ({ group, keys: keys.filter((key) => KEY_GROUPS[key] === group) })).filter(
    (section) => section.keys.length > 0,
  );
}
